import { useFetcher } from '@remix-run/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '~/components/Button';
import Input from '~/components/Forms/Input';
import StatusInputs from '~/components/Forms/StatusInputs';

import useToast, { type ToastData } from '~/hooks/useToast';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { PostLoaderData } from '~/routes/p/$slug';

interface Props {
  firstName?: string;
  lastName?: string;
  onSuccess?: () => void;
}

const ManageAction = ({ firstName, lastName, onSuccess }: Props) => {
  const fetcher = useFetcher<{
    toast: ToastData;
  }>();
  const fetcherData = fetcher.data;
  const isSubmitting = fetcher.state === 'submitting';
  const isSuccess = fetcherData?.toast?.status === 'success';
  const { t } = useTranslation();
  const { site } = useTypedLoaderData<PostLoaderData>();

  useEffect(() => {
    if (isSuccess && onSuccess) {
      onSuccess();
    }
  }, [isSuccess, onSuccess]);

  useToast(fetcherData?.toast);

  return (
    <fetcher.Form method="patch" action="/profile" className="relative">
      <div className="space-y-6">
        <div className="space-y-3">
          <Input
            name="first_name"
            autoComplete="given-name"
            label={`${t('manage.personal_details_form.first_name')}`}
            placeholder={`${t('manage.personal_details_form.first_name')}`}
            defaultValue={firstName}
            textColor={site.primary_theme.text_on_background_color}
          />
          <Input
            name="last_name"
            autoComplete="family-name"
            label={`${t('manage.personal_details_form.last_name')}`}
            placeholder={`${t('manage.personal_details_form.last_name')}`}
            defaultValue={lastName}
            textColor={site.primary_theme.text_on_background_color}
          />
          <StatusInputs name="profile" errorMessage={t('errors.default')} successMessage={t('toast.success')} />
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            style={{
              color: site.primary_theme.text_on_primary_color,
              backgroundColor: site.primary_theme.primary_color,
            }}
          >
            {isSubmitting ? `${t('shared.actions.submitting')}...` : t('shared.cta.submit')}
          </Button>
        </div>
      </div>
    </fetcher.Form>
  );
};

export default ManageAction;
